import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import { Checkbox, CheckboxProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect } from 'react';

interface CheckboxTsoPqProps extends CheckboxProps {
  type?: 'primary' | 'neutral';
  size?: 'small' | 'medium' | 'large';
  enableSquareWripple?: boolean;
  shouldHaveWhiteBackground?: boolean;
  setValue?: any;
  name?: string;
  checked?: boolean;
  watch?: any;
}

const StyledCheckbox = styled(Checkbox, {
  shouldForwardProp: (prop) =>
    !['type', 'size', 'enableSquareWripple', 'shouldHaveWhiteBackground'].includes(prop as string),
})<CheckboxTsoPqProps>(({
  type,
  size,
  enableSquareWripple,
  defaultChecked,
  indeterminate,
  disabled,
  shouldHaveWhiteBackground = false,
}) => {
  // default values
  type = type || 'primary';
  size = size || 'medium';

  const colorMap: { [key: string]: string } = {
    primary: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_enabled,
    neutral: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_enabled,
  };

  const hoverMap: { [key: string]: string } = {
    primary: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_hover,
    neutral: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_hover,
  };

  const sizeMap: { [key: string]: string } = { small: '1.1675rem', medium: '1.5rem', large: '1.835rem' };

  const paddingMap: { [key: string]: string } = { small: '0.4375rem', medium: '0.4375rem', large: '0.5625rem' };

  const disabledBackgroundColor = tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_disabled;
  const disabledFontColor = tsoPqTheme.palette.customTextColor.color_font_ft_nrtrl_disabled;
  const radialGradient = `radial-gradient(circle, ${disabledFontColor} 0%, ${disabledFontColor} 50%, transparent 51%);`;
  const activeBorderRadiusMap = '4px';

  return {
    color: colorMap[type],
    '&.MuiCheckbox-colorPrimary': { color: colorMap[type] },
    '& .MuiSvgIcon-root': {
      fontSize: sizeMap[size],
      background: (disabled && defaultChecked) || (disabled && indeterminate) ? radialGradient : 'none',
      backgroundColor: shouldHaveWhiteBackground
        ? tsoPqTheme.palette.customTextColor.color_font_ft_white
        : 'transparent',
      padding: !disabled && enableSquareWripple ? paddingMap[size] : '',
      borderRadius: enableSquareWripple && activeBorderRadiusMap,
    },
    '&.Mui-disabled': { color: defaultChecked || indeterminate ? disabledBackgroundColor : disabledFontColor },
    '&:hover': { backgroundColor: 'transparent', color: hoverMap[type] },
  };
});

const CheckboxTsoPq = ({
  type,
  size,
  enableSquareWripple,
  setValue,
  name,
  checked,
  watch,
  ...props
}: CheckboxTsoPqProps) => {
  useEffect(() => {
    if (setValue) {
      setValue(name, checked);
    }
  }, [checked, setValue, name]);

  return (
    <StyledCheckbox
      {...props}
      checked={checked}
      type={type}
      size={size}
      enableSquareWripple={enableSquareWripple}
      disableRipple={enableSquareWripple}
    />
  );
};

export default CheckboxTsoPq;
